import React from 'react';
import {
  MapPin,
  Clock,
  User,
  Phone,
  CheckCircle2,
  Navigation,
  MapPinned,
  LocateFixed,
  Shuffle,
  Truck,
} from 'lucide-react';
import ShipmentMediaGallery from '../shipment/ShipmentMediaGallery.js';
import { shipmentStatusLabel } from '../../utils/shipmentStatusLabel.js';

export const getStatusBadgeColor = (status) => {
  const statusColors = {
    booked: 'bg-blue-100 text-blue-800 border-blue-200',
    dispatched: 'bg-purple-100 text-purple-800 border-purple-200',
    received: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    delivered: 'bg-green-100 text-green-800 border-green-200',
    returned: 'bg-red-100 text-red-800 border-red-200',
    rejected: 'bg-rose-100 text-rose-800 border-rose-200',
    cancelled: 'bg-rose-100 text-rose-800 border-rose-200',
  };
  return statusColors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const formatDateTime = (value) =>
  new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatDate = (value) =>
  new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });

const formatBookingAt = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const DetailCard = ({ label, value, accent = 'border-gray-200', icon: Icon, className = '', children }) => (
  <div
    className={`bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 border-l-[3px] sm:border-l-4 ${accent} p-2 sm:p-4 min-w-0 ${className}`}
  >
    <div className="flex items-center gap-1 mb-0.5 sm:mb-1">
      {Icon && <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-400 shrink-0" />}
      <p className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wide truncate">{label}</p>
    </div>
    {children || (
      <p className="text-xs sm:text-base font-semibold text-gray-800 break-words leading-snug">{value ?? 'N/A'}</p>
    )}
  </div>
);

const HubContactLine = ({ contact }) => {
  if (!contact) return null;
  return (
    <a
      href={`tel:${contact}`}
      className="mt-0.5 inline-flex items-center justify-center gap-1 text-[10px] sm:text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline break-all"
    >
      <Phone className="w-2.5 h-2.5 sm:w-3 sm:h-3 shrink-0" />
      {contact}
    </a>
  );
};

const CompactRouteStrip = ({
  from,
  to,
  fromName,
  fromAddress,
  toName,
  toAddress,
  fromLabel,
  toLabel,
  fromContact,
  toContact,
  fromPersonContact,
  toPersonContact,
}) => (
  <div className="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 via-white to-green-50 p-2.5 sm:p-3 md:p-3 animate-trackingSlideUp">
    <div className="flex items-stretch gap-1.5 sm:gap-3 md:gap-4 min-w-0">
      <div className="flex-1 min-w-0 text-center md:text-left">
        <div className="mx-auto md:mx-0 w-8 h-8 sm:w-10 sm:h-10 md:w-9 md:h-9 rounded-full bg-blue-500 text-white flex items-center justify-center shadow-sm animate-trackingPulse">
          <MapPinned className="w-4 h-4 sm:w-5 sm:h-5 md:w-4 md:h-4" />
        </div>
        <p className="text-[10px] sm:text-xs font-semibold text-blue-700 mt-1.5 uppercase">{fromLabel || 'From'}</p>
        <p className="text-[11px] sm:text-sm md:text-sm font-bold text-gray-800 break-words line-clamp-2 mt-0.5">{from || 'N/A'}</p>
        <HubContactLine contact={fromContact} />
        <div className="hidden md:block mt-1 min-w-0">
          <p className="text-xs text-gray-700 font-medium break-words">{fromName || 'N/A'}</p>
          {fromPersonContact ? (
            <a
              href={`tel:${fromPersonContact}`}
              className="text-[11px] text-blue-600 hover:text-blue-700 hover:underline break-all leading-snug mt-0.5 inline-block"
            >
              {fromPersonContact}
            </a>
          ) : (
            <p className="text-[11px] text-gray-500 break-words line-clamp-2 leading-snug mt-0.5">{fromAddress || 'N/A'}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center justify-center px-0.5 sm:px-2 md:px-1 shrink-0 self-center">
        <Navigation className="w-4 h-4 sm:w-5 sm:h-5 md:w-4 md:h-4 text-orange-500 rotate-90 sm:rotate-0 animate-trackingPulse" />
        <div className="h-0.5 w-8 sm:w-16 md:w-12 mt-1 rounded-full overflow-hidden bg-orange-100">
          <div className="h-full w-full animate-routeDash bg-[length:8px_2px] bg-repeat-x bg-[linear-gradient(90deg,#fb923c_50%,transparent_50%)]" />
        </div>
      </div>

      <div className="flex-1 min-w-0 text-center md:text-right">
        <div className="mx-auto md:ml-auto md:mr-0 w-8 h-8 sm:w-10 sm:h-10 md:w-9 md:h-9 rounded-full bg-green-500 text-white flex items-center justify-center shadow-sm">
          <LocateFixed className="w-4 h-4 sm:w-5 sm:h-5 md:w-4 md:h-4" />
        </div>
        <p className="text-[10px] sm:text-xs font-semibold text-green-700 mt-1.5 uppercase">{toLabel || 'To'}</p>
        <p className="text-[11px] sm:text-sm md:text-sm font-bold text-gray-800 break-words line-clamp-2 mt-0.5">{to || 'N/A'}</p>
        <HubContactLine contact={toContact} />
        <div className="hidden md:block mt-1 min-w-0">
          <p className="text-xs text-gray-700 font-medium break-words">{toName || 'N/A'}</p>
          {toPersonContact ? (
            <a
              href={`tel:${toPersonContact}`}
              className="text-[11px] text-blue-600 hover:text-blue-700 hover:underline break-all leading-snug mt-0.5 inline-block"
            >
              {toPersonContact}
            </a>
          ) : (
            <p className="text-[11px] text-gray-500 break-words line-clamp-2 leading-snug mt-0.5">{toAddress || 'N/A'}</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

const ProofButton = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 sm:px-3 sm:py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-[11px] sm:text-sm font-medium"
  >
    {label}
  </button>
);

const deliveryProofs = (entry) => {
  const proofs = [];
  if (entry?.has_delivery_receiver_image) {
    proofs.push({
      label: 'View Receiver Photo',
      url: entry.delivery_receiver_image_url || entry.delivery_receiver_image,
    });
  }
  if (entry?.has_delivery_gov_id_image) {
    proofs.push({
      label: 'View Government ID',
      url: entry.delivery_gov_id_image_url || entry.delivery_gov_id_image,
    });
  }
  return proofs.filter((item) => item.url);
};

const DeliveryConfirmation = ({ entry, onViewDeliveryImage }) => {
  const isDelivered = entry.status?.toLowerCase() === 'delivered';
  const proofs = deliveryProofs(entry);
  const hasInfo =
    entry.delivery_receiver ||
    entry.delivery_receiver_contact ||
    proofs.length > 0;

  if (!isDelivered || !hasInfo) return null;

  return (
    <div className="col-span-2 bg-green-50 border border-green-100 rounded-lg sm:rounded-xl p-2 sm:p-3">
      <p className="text-[11px] sm:text-xs font-semibold text-green-800 mb-1.5 sm:mb-2">Delivery Confirmation</p>
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-[11px] sm:text-sm">
        {entry.delivery_receiver && (
          <div className="min-w-0">
            <p className="text-gray-500 font-medium mb-0.5">Received By</p>
            <p className="text-gray-800 break-words">{entry.delivery_receiver}</p>
          </div>
        )}
        {entry.delivery_receiver_contact && (
          <div className="min-w-0">
            <p className="text-gray-500 font-medium mb-0.5">Contact</p>
            <a
              href={`tel:${entry.delivery_receiver_contact}`}
              className="text-blue-600 hover:text-blue-700 hover:underline break-all"
            >
              {entry.delivery_receiver_contact}
            </a>
          </div>
        )}
        {proofs.length > 0 && onViewDeliveryImage && (
          <div className="col-span-2 flex flex-wrap gap-2">
            {proofs.map((proof) => (
              <ProofButton
                key={proof.label}
                label={proof.label}
                onClick={() => onViewDeliveryImage(proof.url, proof.label)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LiveLocationBanner = ({ location, status, timestamp }) => (
  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white p-3 sm:p-4 shadow-md animate-trackingSlideUp">
    <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10 animate-gpsRing" />
    <div className="absolute right-6 bottom-2 w-12 h-12 rounded-full bg-white/10 animate-gpsRing [animation-delay:0.6s]" />

    <div className="relative flex items-start gap-2.5 sm:gap-3">
      <div className="relative shrink-0">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 flex items-center justify-center animate-trackingPulse">
          <Navigation className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-blue-700 animate-locationBlink" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-blue-100 mb-0.5">
          Live GPS Location
        </p>
        <p className="text-sm sm:text-lg font-bold break-words leading-tight animate-locationBlink">{location}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] sm:text-xs text-blue-100">
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3 h-3 shrink-0" />
            {status}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3 shrink-0" />
            {formatDateTime(timestamp)}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const DeliveredBanner = ({ location, timestamp, receiver, contact, proofs = [], onViewDeliveryImage }) => (
  <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-600 to-green-700 text-white p-3 sm:p-4 shadow-md animate-trackingSlideUp">
    <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/10" />
    <div className="absolute right-6 bottom-2 w-12 h-12 rounded-full bg-white/10" />

    <div className="relative flex items-start gap-2.5 sm:gap-3">
      <div className="shrink-0">
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20 flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-green-100 mb-0.5">
          Delivered Successfully
        </p>
        <p className="text-sm sm:text-lg font-bold break-words leading-tight">{location}</p>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[11px] sm:text-xs text-green-100">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3 shrink-0" />
            {formatDateTime(timestamp)}
          </span>
          {receiver && (
            <span className="inline-flex items-center gap-1">
              <User className="w-3 h-3 shrink-0" />
              {receiver}
            </span>
          )}
          {contact && (
            <a href={`tel:${contact}`} className="inline-flex items-center gap-1 hover:underline">
              <Phone className="w-3 h-3 shrink-0" />
              {contact}
            </a>
          )}
        </div>
        {proofs.length > 0 && onViewDeliveryImage && (
          <div className="mt-2 flex flex-wrap gap-2">
            {proofs.map((proof) => (
              <button
                key={proof.label}
                type="button"
                onClick={() => onViewDeliveryImage(proof.url, proof.label)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-[11px] sm:text-xs font-medium transition-colors"
              >
                {proof.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const CurrentLocationCard = ({ location }) => (
  <div className="relative bg-blue-50 rounded-lg sm:rounded-xl shadow-sm border border-blue-200 border-l-[3px] sm:border-l-4 border-l-blue-500 p-2 sm:p-4 min-w-0 animate-locationGlow">
    <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-blue-500 animate-locationBlink" aria-hidden="true" />
    <div className="flex items-center gap-1 mb-0.5 sm:mb-1">
      <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600 shrink-0 animate-trackingPulse" />
      <p className="text-[10px] sm:text-xs text-blue-600 font-semibold uppercase tracking-wide truncate">Location</p>
    </div>
    <p className="text-xs sm:text-base font-bold text-blue-800 break-words leading-snug animate-locationBlink">
      {location ?? 'N/A'}
    </p>
  </div>
);

function remarkForViewer(remarks, hideVehicle) {
  if (!remarks || !hideVehicle) return remarks;
  return String(remarks).replace(/\s+with vehicle\s+\S+/gi, '').replace(/\s{2,}/g, ' ').trim();
}

const ItclDetails = ({ itcl, hideVehicle = false }) => {
  if (!itcl) return null;
  const phaseLabel = {
    loading: 'Loading',
    approved: 'Approved',
    unloading: 'Unloading',
    rejected: 'Rejected',
    update: 'Update',
  }[itcl.phase] || 'ITCL';
  return (
    <div className="col-span-2 bg-white border border-indigo-200 rounded-lg p-2 sm:p-3">
      <p className="text-[11px] sm:text-xs font-semibold text-indigo-800 mb-1.5 sm:mb-2 flex items-center gap-1">
        <Truck className="w-3 h-3" /> ITCL {phaseLabel}
        {itcl.request_id ? ` · ${itcl.request_id}` : ''}
      </p>
      <div className="grid grid-cols-2 gap-2 text-[10px] sm:text-xs text-gray-700">
        {(itcl.from_hub || itcl.to_hub) && (
          <p className="col-span-2">
            {(itcl.from_hub || 'N/A')} → {(itcl.to_hub || 'N/A')}
          </p>
        )}
        {!hideVehicle && itcl.vehicle_number && (
          <p><span className="text-gray-500">Vehicle</span> {itcl.vehicle_number}</p>
        )}
        {itcl.driver_name && (
          <p><span className="text-gray-500">Driver</span> {itcl.driver_name}</p>
        )}
        {itcl.status && (
          <p><span className="text-gray-500">Request</span> {itcl.status}</p>
        )}
      </div>
    </div>
  );
};

const EtaAtaStrip = ({ eta, ata }) => {
  if (!eta && !ata) return null;
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-2.5 py-2">
        <p className="text-[10px] uppercase font-semibold text-blue-700">ETA</p>
        <p className="text-xs sm:text-sm font-semibold text-gray-800">{eta ? formatDateTime(eta) : '—'}</p>
      </div>
      <div className="rounded-lg border border-green-100 bg-green-50 px-2.5 py-2">
        <p className="text-[10px] uppercase font-semibold text-green-700">ATA</p>
        <p className="text-xs sm:text-sm font-semibold text-gray-800">{ata ? formatDateTime(ata) : '—'}</p>
      </div>
    </div>
  );
};

const ShipmentTrackingPanel = ({
  trackingData,
  onViewDeliveryImage,
  headerActions = null,
  timelineOnly = false,
  hideVehicle = false,
}) => {
  if (!trackingData) return null;

  const { shipment_details: details, current_location: current, tracking_timeline: timeline } =
    trackingData;
  const eta = trackingData.eta || details?.eta || current?.eta;
  const ata = trackingData.ata || details?.ata || current?.ata;

  const isDelivered = current?.status?.toLowerCase() === 'delivered';

  return (
    <div className="space-y-2.5 sm:space-y-4 min-w-0">
      <EtaAtaStrip eta={eta} ata={ata} />
      {!timelineOnly && current &&
        (isDelivered ? (
          <DeliveredBanner
            location={current.location}
            timestamp={current.timestamp}
            receiver={current.delivery_receiver}
            contact={current.delivery_receiver_contact}
            proofs={deliveryProofs(current)}
            onViewDeliveryImage={onViewDeliveryImage}
          />
        ) : (
          <LiveLocationBanner
            location={current.location}
            status={current.status}
            timestamp={current.timestamp}
          />
        ))}

      {!timelineOnly && (
      <div className="bg-white rounded-xl shadow-md p-2.5 sm:p-4 md:p-5 min-w-0 animate-trackingSlideUp [animation-delay:0.05s]">
        <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 pb-2.5 sm:pb-3 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-sm sm:text-lg font-bold text-gray-800 flex items-center gap-1.5">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 shrink-0" />
              Shipment Details
            </h2>
            <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5">Live tracking summary</p>
          </div>
          {headerActions && (
            <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full sm:w-auto shrink-0">
              {headerActions}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-1.5 sm:gap-3">
          <DetailCard label="Shipment ID" accent="border-orange-500" icon={MapPinned} className="col-span-2 lg:col-span-1">
            <p className="text-xs sm:text-base font-bold text-orange-600 font-mono break-all leading-snug">
              #{details.shipment_id}
            </p>
            {details.consignment_no && String(details.consignment_no) !== String(details.shipment_id) && (
              <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-0.5 break-all">
                Consignment {details.consignment_no}
              </p>
            )}
          </DetailCard>
          <DetailCard label="Status" accent="border-blue-500" icon={Navigation}>
            <span
              className={`inline-flex px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase border ${getStatusBadgeColor(shipmentStatusLabel(details))}`}
            >
              {shipmentStatusLabel(details)}
            </span>
          </DetailCard>
          <DetailCard label="Articles" value={details.no_of_articles || 0} accent="border-purple-500" />
          {Number(details.weight) > 0 && (
            <DetailCard label="Weight" value={`${Number(details.weight)} kg`} accent="border-indigo-500" />
          )}
          {!hideVehicle && (
          <DetailCard label="Vehicle" accent="border-teal-500">
            <p className="text-xs sm:text-base font-semibold text-gray-800 break-words leading-snug">
              {details.vehicle_number || 'N/A'}
            </p>
          </DetailCard>
          )}
          <DetailCard label="Booking at" value={formatBookingAt(details.created_at)} accent="border-gray-400" icon={Clock} />
          {details.expected_delivery_time && (
            <DetailCard label="Office hours" value={details.expected_delivery_time} accent="border-orange-400" icon={Clock} />
          )}
        </div>

        <div className="mt-2.5 sm:mt-3 pt-2.5 sm:pt-3 border-t border-gray-100">
          <CompactRouteStrip
            from={details.sender_location || details.sender_name}
            to={details.receiver_location || details.receiver_name}
            fromName={details.sender_name}
            fromAddress={details.sender_address}
            toName={details.receiver_name}
            toAddress={details.receiver_address}
            fromContact={details.sender_hub_contact}
            toContact={details.receiver_hub_contact}
            fromPersonContact={details.sender_contact}
            toPersonContact={details.receiver_contact}
          />
        </div>

        {(details.images?.length > 0 || details.media?.length > 0 || details.parcel_image_1_url || details.parcel_image_2_url) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <ShipmentMediaGallery
              media={
                details.images?.length
                  ? details.images
                  : details.media?.length
                    ? details.media
                    : [
                        details.parcel_image_1_url && { media_type: 'photo', cdn_url: details.parcel_image_1_url },
                        details.parcel_image_2_url && { media_type: 'photo', cdn_url: details.parcel_image_2_url },
                      ].filter(Boolean)
              }
              title="Parcel Photos & Videos"
              emptyMessage=""
            />
          </div>
        )}
      </div>
      )}

      {!timelineOnly && current && (
        <div className="bg-white rounded-xl shadow-md p-2.5 sm:p-4 md:p-5 border border-blue-100 min-w-0 animate-trackingSlideUp [animation-delay:0.1s]">
          <h2 className="text-sm sm:text-lg font-bold mb-2 sm:mb-3 flex items-center gap-1.5 text-gray-800">
            {isDelivered ? (
              <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 shrink-0" />
            ) : (
              <LocateFixed className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 shrink-0 animate-trackingPulse" />
            )}
            {isDelivered ? 'Delivery Details' : 'Location Details'}
          </h2>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-1.5 sm:gap-3">
            {isDelivered ? (
              <DetailCard label="Delivered At" accent="border-green-500" icon={MapPin}>
                <p className="text-xs sm:text-base font-bold text-green-800 break-words leading-snug">
                  {current.location ?? 'N/A'}
                </p>
              </DetailCard>
            ) : (
              <CurrentLocationCard location={current.location} />
            )}
            <DetailCard label="Status" value={current.status} accent={isDelivered ? 'border-green-400' : 'border-blue-400'} icon={Navigation} />
            <DetailCard label="Updated By" value={current.updated_by} accent={isDelivered ? 'border-green-300' : 'border-blue-300'} icon={User} />
            <DetailCard
              label={isDelivered ? 'Delivered On' : 'Last Updated'}
              value={formatDateTime(current.timestamp)}
              accent={isDelivered ? 'border-green-200' : 'border-blue-200'}
              icon={Clock}
            />
          </div>
        </div>
      )}

      {timeline && timeline.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-2.5 sm:p-4 md:p-5 min-w-0 animate-trackingSlideUp [animation-delay:0.15s]">
          <h2 className="text-sm sm:text-lg font-bold text-gray-800 mb-2 sm:mb-4 pb-2 sm:pb-3 border-b border-gray-200 flex items-center gap-1.5">
            <Navigation className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 shrink-0" />
            Tracking Timeline
          </h2>

          <div className="space-y-2 sm:space-y-4">
            {timeline.map((entry, index) => {
              const isCrossing = entry.event_type === 'crossing';
              const isCrossingUnload = entry.event_type === 'crossing_unload';
              const isCrossingEvent = isCrossing || isCrossingUnload;
              const isItcl = entry.event_type === 'itcl' || Boolean(entry.itcl);
              return (
              <div
                key={entry.id || index}
                className="flex gap-2 sm:gap-3 min-w-0 animate-trackingSlideUp"
                style={{ animationDelay: `${0.08 * index + 0.2}s` }}
              >
                <div className="flex flex-col items-center shrink-0 pt-0.5">
                  <div
                    className={`relative w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-white shadow-sm ${
                      isItcl ? 'bg-indigo-500' : isCrossingEvent ? 'bg-amber-500' : index === 0 ? 'bg-orange-500' : 'bg-green-500'
                    }`}
                  >
                    {index === 0 && !isCrossingEvent && !isItcl && (
                      <span className="absolute inset-0 rounded-full bg-orange-400 animate-gpsRing opacity-60" />
                    )}
                    {isItcl ? (
                      <Truck className="w-3 h-3 sm:w-4 sm:h-4 relative z-10" />
                    ) : isCrossingEvent ? (
                      <Shuffle className="w-3 h-3 sm:w-4 sm:h-4 relative z-10" />
                    ) : index === 0 ? (
                      <Navigation className="w-3 h-3 sm:w-4 sm:h-4 relative z-10" />
                    ) : (
                      <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 relative z-10" />
                    )}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gradient-to-b from-orange-200 to-green-200 mt-0.5 min-h-[28px] sm:min-h-[40px]" />
                  )}
                </div>

                <div
                  className={`flex-1 min-w-0 rounded-lg sm:rounded-xl p-2 sm:p-4 border transition-colors ${
                    isItcl
                      ? 'bg-indigo-50 border-indigo-200 hover:border-indigo-300'
                      : isCrossingEvent
                      ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-1.5 sm:gap-3 mb-1.5 sm:mb-3">
                    <h3 className="font-bold text-gray-900 text-xs sm:text-base break-words leading-snug flex items-center gap-1.5">
                      {isItcl && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide">
                          <Truck className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> ITCL
                        </span>
                      )}
                      {isCrossingUnload && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide">
                          <Shuffle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Crossing Unload
                        </span>
                      )}
                      {isCrossing && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[9px] sm:text-[10px] font-bold uppercase tracking-wide">
                          <Shuffle className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Crossing
                        </span>
                      )}
                      {isItcl
                        ? `ITCL ${entry.itcl?.phase === 'loading' ? 'Loading' : entry.itcl?.phase === 'unloading' ? 'Unloading' : entry.itcl?.phase === 'approved' ? 'Approved' : entry.itcl?.phase === 'rejected' ? 'Rejected' : 'Update'} (${shipmentStatusLabel(entry.status)})`
                        : isCrossingUnload
                          ? `Unloaded at hub (${shipmentStatusLabel(entry.status)})`
                          : isCrossing
                            ? `Vehicle Changed (${shipmentStatusLabel(entry.status)})`
                            : shipmentStatusLabel(entry.status)}
                    </h3>
                    <span className="inline-flex items-center gap-0.5 text-[10px] sm:text-xs text-gray-500 shrink-0">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(entry.timestamp)}
                    </span>
                  </div>

                  <div className="flex items-start gap-2 sm:gap-3">
                  <div className="grid flex-1 min-w-0 grid-cols-2 gap-1.5 sm:gap-3 text-[11px] sm:text-sm">
                    <div className="min-w-0 col-span-2 sm:col-span-1">
                      <p className="text-gray-500 font-medium mb-0.5 flex items-center gap-1">
                        <MapPin
                          className={`w-3 h-3 shrink-0 text-orange-500 ${index === 0 ? 'animate-trackingPulse' : ''}`}
                        />{' '}
                        Location
                      </p>
                      {entry.maps_url ? (
                        <>
                          <a
                            href={entry.maps_url}
                            target="_blank"
                            rel="noreferrer"
                            title="Open location on map"
                            className={`inline-flex max-w-full items-center gap-1 mt-0.5 text-blue-700 hover:underline break-words font-medium ${
                              index === 0 ? 'font-bold animate-locationBlink' : ''
                            }`}
                          >
                            <span className="break-words">{entry.location}</span>
                          </a>
                        </>
                      ) : (
                        <p
                          className={`text-gray-800 break-words font-medium ${
                            index === 0 ? 'text-blue-700 font-bold animate-locationBlink' : ''
                          }`}
                        >
                          {entry.location}
                        </p>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-gray-500 font-medium mb-0.5 flex items-center gap-1">
                        <User className="w-3 h-3 shrink-0" /> Updated by
                      </p>
                      <p className="text-gray-800 break-words">
                        {entry.updated_by_name}
                      </p>
                      <p className="text-[10px] text-gray-500">({entry.updated_by_role})</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-gray-500 font-medium mb-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3 shrink-0" /> Hub contact
                      </p>
                      {(entry.location_hub_contact || entry.updated_by_contact) &&
                      (entry.location_hub_contact || entry.updated_by_contact) !== 'N/A' ? (
                        <a
                          href={`tel:${entry.location_hub_contact || entry.updated_by_contact}`}
                          className="text-blue-600 hover:text-blue-700 hover:underline break-all"
                        >
                          {entry.location_hub_contact || entry.updated_by_contact}
                        </a>
                      ) : (
                        <p className="text-gray-800">N/A</p>
                      )}
                    </div>

                    {isItcl && <ItclDetails itcl={entry.itcl} hideVehicle={hideVehicle} />}

                    {isCrossing && entry.crossing && (
                      <div className="col-span-2 bg-white border border-amber-200 rounded-lg p-2 sm:p-3">
                        <p className="text-[11px] sm:text-xs font-semibold text-amber-800 mb-1.5 sm:mb-2 flex items-center gap-1">
                          <Shuffle className="w-3 h-3" /> Vehicle Crossing
                        </p>
                        {!hideVehicle && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="flex-1 min-w-0 text-center">
                            <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase mb-0.5">From Vehicle</p>
                            <p className="text-xs sm:text-sm font-bold text-gray-700 break-words line-through decoration-red-400">
                              {entry.crossing.previous_vehicle_number || 'N/A'}
                            </p>
                          </div>
                          <Shuffle className="w-4 h-4 text-amber-500 shrink-0" />
                          <div className="flex-1 min-w-0 text-center">
                            <p className="text-[10px] sm:text-xs text-gray-500 font-medium uppercase mb-0.5">To Vehicle</p>
                            <p className="text-xs sm:text-sm font-bold text-green-700 break-words">
                              {entry.crossing.new_vehicle_number || 'N/A'}
                            </p>
                          </div>
                        </div>
                        )}
                        {(entry.crossing.crossing_city || entry.crossing.target_city) && (
                          <div className="mt-2 pt-2 border-t border-amber-100 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[10px] sm:text-xs text-gray-600">
                            {entry.crossing.crossing_city && (
                              <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-amber-500" /> Crossed at {entry.crossing.crossing_city}
                              </span>
                            )}
                            {entry.crossing.target_city && (
                              <span className="inline-flex items-center gap-1">
                                <Navigation className="w-3 h-3 text-green-500" /> Onward to {entry.crossing.target_city}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <DeliveryConfirmation entry={entry} onViewDeliveryImage={onViewDeliveryImage} />

                    {remarkForViewer(entry.remarks, hideVehicle) && (
                      <p className="col-span-2 min-w-0 text-gray-700 break-words whitespace-pre-wrap leading-relaxed text-[11px] sm:text-sm">
                        <span className="font-semibold text-gray-800">Remark: </span>
                        {remarkForViewer(entry.remarks, hideVehicle)}
                      </p>
                    )}
                  </div>
                  {entry.maps_url && (
                    <a
                      href={entry.maps_url}
                      target="_blank"
                      rel="noreferrer"
                      title="Open location on map"
                      className="shrink-0 block w-[112px] h-[84px] sm:w-[168px] sm:h-[112px] rounded-md border border-blue-200 overflow-hidden bg-gray-100"
                    >
                      <iframe
                        title={`Map preview of ${entry.location || 'hub'}`}
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(
                          entry.latitude != null && entry.longitude != null
                            ? `${entry.latitude},${entry.longitude}`
                            : entry.location || '',
                        )}&z=15&output=embed`}
                        className="w-full h-full pointer-events-none border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </a>
                  )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentTrackingPanel;
